import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";

const AlertsPanel = ({
  alerts,
  loading,
  error,
  fetchAlerts,
  resolveAlert,
  checkExpiring,
  locationId,
  isAdmin
}) => {
  // Filter state
  const [filterType, setFilterType] = useState("ALL");
  const [showResolved, setShowResolved] = useState(false);

  // Modal states
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolving, setResolving] = useState(false);

  // Check expiring modal
  const [showCheckExpiringModal, setShowCheckExpiringModal] = useState(false);
  const [daysAhead, setDaysAhead] = useState(7);
  const [checkingExpiring, setCheckingExpiring] = useState(false);
  const [checkExpiringResult, setCheckExpiringResult] = useState(null);

  // Memoized fetch function
  const fetchData = useCallback(() => {
    if (locationId) {
      const alertType = filterType === "ALL" ? null : filterType;
      fetchAlerts(locationId, alertType, !showResolved);
    }
  }, [locationId, filterType, showResolved, fetchAlerts]);

  // Fetch alerts on mount and when filters change
  useEffect(() => {
    fetchData();

    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchData]);

  // Get alert type styling (handle both uppercase and lowercase)
  const getAlertTypeBadgeClass = (alertType) => {
    const type = alertType?.toUpperCase();
    switch (type) {
      case "FIFO":
        return "badge-danger";
      case "EXPIRY":
        return "badge-warning";
      case "LOW":
      case "LOW_STOCK":
        return "badge-info";
      case "EXPIRED":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  };

  // Get alert type icon (handle both uppercase and lowercase)
  const getAlertTypeIcon = (alertType) => {
    const type = alertType?.toUpperCase();
    switch (type) {
      case "FIFO":
        return "fa-exchange";
      case "EXPIRY":
        return "fa-clock-o";
      case "LOW":
      case "LOW_STOCK":
        return "fa-arrow-down";
      case "EXPIRED":
        return "fa-times-circle";
      default:
        return "fa-exclamation-triangle";
    }
  };

  // Group alerts by type for stats (handle lowercase alert types)
  const getAlertStats = () => {
    const active = alerts.active || [];
    return {
      total: active.length,
      fifo: active.filter(a => a.alert_type?.toUpperCase() === "FIFO").length,
      expiry: active.filter(a => a.alert_type?.toUpperCase() === "EXPIRY").length,
      low: active.filter(a => ["LOW", "LOW_STOCK"].includes(a.alert_type?.toUpperCase())).length,
      expired: active.filter(a => a.alert_type?.toUpperCase() === "EXPIRED").length
    };
  };

  // Filter alerts (handle lowercase alert types)
  const getFilteredAlerts = () => {
    const alertsList = showResolved ? (alerts.resolved || []) : (alerts.active || []);

    if (filterType === "ALL") {
      return alertsList;
    }

    return alertsList.filter(alert => {
      const type = alert.alert_type?.toUpperCase();
      if (filterType === "LOW") {
        return type === "LOW" || type === "LOW_STOCK";
      }
      return type === filterType;
    });
  };

  // Handle resolve alert
  const handleResolve = async () => {
    if (!selectedAlert) return;

    setResolving(true);
    try {
      await resolveAlert(selectedAlert.id);
      setShowResolveModal(false);
      setSelectedAlert(null);
    } catch (err) {
      console.error("Error resolving alert:", err);
    } finally {
      setResolving(false);
    }
  };

  // Handle check expiring
  const handleCheckExpiring = async () => {
    setCheckingExpiring(true);
    setCheckExpiringResult(null);
    try {
      const result = await checkExpiring(locationId, daysAhead);
      setCheckExpiringResult(result);
    } catch (err) {
      console.error("Error checking expiring items:", err);
    } finally {
      setCheckingExpiring(false);
    }
  };

  const stats = getAlertStats();
  const filteredAlerts = getFilteredAlerts();

  return (
    <>
      {/* Summary Stats Row */}
      <div className="row mb-3">
        <div className="col-6 col-md-2">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: stats.total > 0 ? '#ff5454' : '#bdea74' }}>
                {stats.total}
              </h4>
              <small className="text-muted">Active Alerts</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#ff5454' }}>{stats.fifo}</h4>
              <small className="text-muted">FIFO</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#fabb3d' }}>{stats.expiry}</h4>
              <small className="text-muted">Expiring</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#36a9e1' }}>{stats.low}</h4>
              <small className="text-muted">Low Stock</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#ff5454' }}>{stats.expired}</h4>
              <small className="text-muted">Expired</small>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="col-6 col-md-2">
            <div className="card mb-0">
              <div className="card-body p-3 text-center">
                <button
                  className="btn btn-sm btn-warning w-100"
                  onClick={() => setShowCheckExpiringModal(true)}
                  title="Scan for items expiring soon"
                >
                  <i className="fa fa-search mr-05"></i>
                  Check Expiring
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Card */}
      <div className="card">
        {/* Filter Header */}
        <div className="card-header reports-filter">
          <form className="row mb-1">
            <div className="col-12 col-sm-4">
              <label className="col-form-label filter-label" htmlFor="alertType">
                <i className="fa fa-filter mr-05 ml-05"></i>
                Alert Type
              </label>
              <select
                className="form-control"
                id="alertType"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="ALL">All Types</option>
                <option value="FIFO">FIFO Violations</option>
                <option value="EXPIRY">Near Expiration</option>
                <option value="LOW">Low Stock</option>
                <option value="EXPIRED">Expired Stock</option>
              </select>
            </div>
            <div className="col-12 col-sm-4">
              <label className="col-form-label filter-label ml-05" htmlFor="status">
                Status
              </label>
              <select
                className="form-control"
                id="status"
                value={showResolved ? "resolved" : "active"}
                onChange={(e) => setShowResolved(e.target.value === "resolved")}
              >
                <option value="active">Active Alerts</option>
                <option value="resolved">Resolved Alerts</option>
              </select>
            </div>
            <div className="col-12 col-sm-4 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={() => {
                  setFilterType("ALL");
                  setShowResolved(false);
                }}
              >
                <i className="fa fa-refresh mr-05"></i>
                Reset Filters
              </button>
            </div>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger m-3" role="alert">
            {error}
          </div>
        )}

        {/* Alerts Table */}
        {loading && filteredAlerts.length === 0 ? (
          <div className="card-body text-center py-5">
            <i className="fa fa-spinner fa-spin fa-2x"></i>
            <p className="mt-2 text-muted">Loading alerts...</p>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="shipment-list--empty">
            <div className="fa fa-bell-slash icon" aria-hidden="true"></div>
            <div>
              {showResolved
                ? "No resolved alerts found"
                : "No active alerts - All clear!"}
            </div>
          </div>
        ) : (
          <table className="table table-striped table-bordered datatable mb-0">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>Type</th>
                <th>Message</th>
                <th className="hidden-xs-down" style={{ width: '150px' }}>Created</th>
                {showResolved && (
                  <>
                    <th className="hidden-xs-down" style={{ width: '150px' }}>Resolved</th>
                    <th className="hidden-xs-down" style={{ width: '120px' }}>Resolved By</th>
                  </>
                )}
                {!showResolved && isAdmin && (
                  <th className="text-center" style={{ width: '100px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map((alert) => (
                <tr key={alert.id}>
                  <td>
                    <span className={`badge badge-pill ${getAlertTypeBadgeClass(alert.alert_type)}`}>
                      <i className={`fa ${getAlertTypeIcon(alert.alert_type)} mr-05`}></i>
                      {alert.alert_type_display || alert.alert_type}
                    </span>
                  </td>
                  <td>
                    <div>
                      {alert.message}
                    </div>
                    <small className="text-muted">
                      <i className="fa fa-map-marker mr-05"></i>
                      {alert.location_name || `Location #${alert.location}`}
                    </small>
                  </td>
                  <td className="hidden-xs-down">
                    <div>
                      {moment(alert.created_at).format('MM/DD/YY')}
                      <br />
                      <small className="text-muted">
                        {moment(alert.created_at).format('h:mm A')}
                      </small>
                    </div>
                  </td>
                  {showResolved && (
                    <>
                      <td className="hidden-xs-down">
                        {alert.resolved_at ? (
                          <div>
                            {moment(alert.resolved_at).format('MM/DD/YY')}
                            <br />
                            <small className="text-muted">
                              {moment(alert.resolved_at).format('h:mm A')}
                            </small>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="hidden-xs-down">
                        {alert.resolved_by_name || <span className="text-muted">-</span>}
                      </td>
                    </>
                  )}
                  {!showResolved && isAdmin && (
                    <td className="text-center">
                      <button
                        className="btn-sm btn-success"
                        onClick={() => {
                          setSelectedAlert(alert);
                          setShowResolveModal(true);
                        }}
                        title="Resolve alert"
                      >
                        <i className="fa fa-check"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Refresh indicator */}
        {loading && filteredAlerts.length > 0 && (
          <div className="card-footer text-center py-2">
            <small className="text-muted">
              <i className="fa fa-refresh fa-spin mr-05"></i> Refreshing...
            </small>
          </div>
        )}
      </div>

      {/* Resolve Alert Modal */}
      <Modal show={showResolveModal} onHide={() => setShowResolveModal(false)} dialogClassName="modal-logut" animation={false}>
        <div className="card mb-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>
              <i className="fa fa-check-circle mr-05"></i>
              Resolve Alert
            </span>
            <button type="button" className="btn-close" onClick={() => setShowResolveModal(false)}></button>
          </div>

          <div className="card-body">
            {selectedAlert && (
              <>
                <div className={`alert ${
                  selectedAlert.alert_type === 'FIFO' || selectedAlert.alert_type === 'EXPIRED'
                    ? 'alert-danger'
                    : selectedAlert.alert_type === 'EXPIRY'
                      ? 'alert-warning'
                      : 'alert-info'
                }`}>
                  <strong>
                    <i className={`fa ${getAlertTypeIcon(selectedAlert.alert_type)} mr-05`}></i>
                    {selectedAlert.alert_type_display || selectedAlert.alert_type}
                  </strong>
                  <p className="mb-0 mt-2">{selectedAlert.message}</p>
                </div>

                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <td><strong>Location</strong></td>
                      <td>{selectedAlert.location_name}</td>
                    </tr>
                    <tr>
                      <td><strong>Created</strong></td>
                      <td>{moment(selectedAlert.created_at).format('MMM DD, YYYY h:mm A')}</td>
                    </tr>
                    <tr>
                      <td><strong>Time Ago</strong></td>
                      <td>{moment(selectedAlert.created_at).fromNow()}</td>
                    </tr>
                  </tbody>
                </table>

                <p className="text-muted mt-3 mb-0">
                  <i className="fa fa-info-circle mr-05"></i>
                  Marking this alert as resolved will remove it from active alerts.
                  Your username will be recorded as the resolver.
                </p>
              </>
            )}
          </div>

          <div className="card-footer text-end">
            <button
              className="btn btn-secondary mr-05"
              onClick={() => setShowResolveModal(false)}
              disabled={resolving}
            >
              Cancel
            </button>
            <button
              className="btn btn-success"
              onClick={handleResolve}
              disabled={resolving}
            >
              {resolving ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-05"></i>
                  Resolving...
                </>
              ) : (
                <>
                  <i className="fa fa-check mr-05"></i>
                  Resolve Alert
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Check Expiring Items Modal */}
      <Modal show={showCheckExpiringModal} onHide={() => {
        setShowCheckExpiringModal(false);
        setCheckExpiringResult(null);
      }} dialogClassName="modal-logut" animation={false}>
        <div className="card mb-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>
              <i className="fa fa-clock-o mr-05"></i>
              Check Expiring Items
            </span>
            <button type="button" className="btn-close" onClick={() => {
              setShowCheckExpiringModal(false);
              setCheckExpiringResult(null);
            }}></button>
          </div>

          <div className="card-body">
            {!checkExpiringResult ? (
              <>
                <p>
                  Scan stock for items that are expiring soon. This will create alerts
                  for any items found.
                </p>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Days Ahead</strong>
                  </label>
                  <select
                    className="form-control"
                    value={daysAhead}
                    onChange={(e) => setDaysAhead(parseInt(e.target.value))}
                    disabled={checkingExpiring}
                  >
                    <option value={3}>3 days</option>
                    <option value={5}>5 days</option>
                    <option value={7}>7 days (default)</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                  <small className="text-muted">
                    Check for items expiring within this many days
                  </small>
                </div>
              </>
            ) : (
              <div>
                <div className={`alert ${checkExpiringResult.alerts_created > 0 ? 'alert-warning' : 'alert-success'}`}>
                  <strong>
                    <i className={`fa ${checkExpiringResult.alerts_created > 0 ? 'fa-exclamation-triangle' : 'fa-check-circle'} mr-05`}></i>
                    Scan Complete
                  </strong>
                  <p className="mb-0 mt-2">
                    Found <strong>{checkExpiringResult.expiring_items}</strong> expiring items.
                    <br />
                    Created <strong>{checkExpiringResult.alerts_created}</strong> new alerts.
                  </p>
                </div>

                {checkExpiringResult.alerts && checkExpiringResult.alerts.length > 0 && (
                  <div className="mt-3">
                    <strong>New Alerts Created:</strong>
                    <table className="table table-sm table-striped mt-2 mb-0">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkExpiringResult.alerts.map((alert) => (
                          <tr key={alert.id}>
                            <td>
                              <small>{alert.message}</small>
                            </td>
                            <td>
                              <span className={`badge badge-pill ${getAlertTypeBadgeClass(alert.alert_type)}`}>
                                {alert.alert_type_display}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card-footer text-end">
            {!checkExpiringResult ? (
              <>
                <button
                  className="btn btn-secondary mr-05"
                  onClick={() => setShowCheckExpiringModal(false)}
                  disabled={checkingExpiring}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleCheckExpiring}
                  disabled={checkingExpiring}
                >
                  {checkingExpiring ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-05"></i>
                      Scanning...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-search mr-05"></i>
                      Start Scan
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCheckExpiringModal(false);
                  setCheckExpiringResult(null);
                }}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AlertsPanel;
